import { Link, useLocation } from "wouter";
import { LayoutDashboard, ListTodo, MessageCircle, Building2, FolderOpen, LogOut, Briefcase, Radio, Sun, Moon, Mail, Calendar, Sparkles, Inbox, Users } from "lucide-react";
import { useState, useEffect } from "react";
import { CohortProvider } from "@/lib/cohort-context";
import { CohortSwitcher } from "@/components/cohort-switcher";
import { PortalSwitcher } from "@/components/portal-switcher";

const BASE = import.meta.env.BASE_URL;

export default function BankerLayout({ children }: { children: React.ReactNode }) {
  const [location, navigate] = useLocation();
  const [banker, setBanker] = useState<any>(null);
  const [pending, setPending] = useState(0);
  const [unread, setUnread] = useState(0);
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof window === "undefined") return "dark";
    return (localStorage.getItem("banker_theme") as "dark" | "light") || "light";
  });

  useEffect(() => {
    localStorage.setItem("banker_theme", theme);
  }, [theme]);

  useEffect(() => {
    const data = localStorage.getItem("banker_data");
    if (data) { try { setBanker(JSON.parse(data)); } catch {} }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const [r, rn] = await Promise.all([
          fetch(`${BASE}api/banker/dashboard`, { headers: { "x-banker-token": token } }),
          fetch(`${BASE}api/banker/me/notifications/counts`, { headers: { "x-banker-token": token } }),
        ]);
        if (r.ok && !cancelled) {
          const d = await r.json();
          setPending((d.tasksPending || 0) + (d.tasksInProgress || 0));
        }
        if (rn.ok && !cancelled) {
          const c = await rn.json();
          setUnread(c.total || 0);
        }
      } catch {}
    };
    fetchStats();
    const iv = setInterval(fetchStats, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem("banker_token");
    localStorage.removeItem("banker_data");
    localStorage.removeItem("banker_active_cohort");
    navigate("/banker/login");
  };

  const nav = [
    { href: "/banker", label: "Dashboard", icon: LayoutDashboard, exact: true },
    { href: "/banker/mail", label: "Mail", icon: Mail, badge: unread },
    { href: "/banker/conversations", label: "Conversations", icon: Inbox },
    { href: "/banker/tasks", label: "Tasks", icon: ListTodo, badge: pending },
    { href: "/banker/calendar", label: "Calendar", icon: Calendar },
    { href: "/banker/meera", label: "Ask Meera", icon: MessageCircle },
    // My founders is the new advisor-pipe primary triage surface (Phase 1.0).
    // Placed BEFORE Portfolio companies so the advisor lands on their own
    // founders first, not the cohort-wide list.
    { href: "/banker/my-founders", label: "My founders", icon: Users },
    { href: "/banker/companies", label: "Portfolio companies", icon: Building2 },
    { href: "/banker/investor-signals", label: "Investor Signals", icon: Radio },
    { href: "/banker/artifacts", label: "Artifacts", icon: FolderOpen },
    { href: "/banker/originations", label: "My originations", icon: Sparkles },
  ] as any[];

  const toggleTheme = () => setTheme(t => t === "dark" ? "light" : "dark");
  const ThemeIcon = theme === "dark" ? Sun : Moon;

  return (
    <CohortProvider identity="banker">
    <div data-banker-theme={theme} className="flex min-h-screen w-full bg-[#0A0A0A] text-white">
      <aside className="w-64 bg-[#111111] border-r border-white/[0.06] flex-shrink-0 flex-col hidden md:flex">
        <div className="p-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg tracking-tight font-bold text-white">Favcy Advisors</h1>
              <p className="text-white/30 text-[10px] font-mono uppercase tracking-[0.2em] leading-none">IB Panel</p>
            </div>
          </div>
        </div>
        {banker && (
          <div className="px-4 py-3 border-b border-white/[0.06]">
            <p className="text-sm font-medium text-white/80 truncate">{banker.name}</p>
            <p className="text-[10px] text-white/30 truncate">{banker.title || banker.email}</p>
          </div>
        )}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {nav.map((item) => {
            const active = item.exact ? location === item.href : location.startsWith(item.href);
            const Icon = item.icon;
            return (
              <Link key={item.href} href={item.href}>
                <a className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${active ? "bg-[#FF5C00]/15 text-[#FF5C00]" : "text-white/60 hover:text-white hover:bg-white/[0.04]"}`}>
                  <Icon className="w-4 h-4" />
                  <span className="flex-1">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="bg-[#FF5C00] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </a>
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-white/[0.06] flex items-stretch">
          <button onClick={toggleTheme} title={`Switch to ${theme === "dark" ? "day" : "night"} mode`}
            className="flex items-center justify-center px-4 py-4 text-white/60 hover:text-white hover:bg-white/[0.04] border-r border-white/[0.06]">
            <ThemeIcon className="w-4 h-4" />
          </button>
          <button onClick={handleLogout} className="flex-1 flex items-center gap-3 px-4 py-4 text-white/60 hover:text-white hover:bg-white/[0.04] text-sm">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden w-full bg-[#111111] border-b border-white/[0.06] px-4 py-3 flex items-center justify-between fixed top-0 left-0 right-0 z-40">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center">
            <Briefcase className="w-3.5 h-3.5 text-white" />
          </div>
          <p className="font-bold text-sm">Favcy Advisors</p>
        </div>
        <div className="flex items-center gap-3">
          <Link href="/banker/mail">
            <a title="Mail" className="relative text-white/60 hover:text-white">
              <Mail className="w-4 h-4" />
              {unread > 0 && <span className="absolute -top-1 -right-1.5 bg-[#FF5C00] text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-1">{unread > 99 ? "99+" : unread}</span>}
            </a>
          </Link>
          <button onClick={toggleTheme} title={`Switch to ${theme === "dark" ? "day" : "night"} mode`} className="text-white/60 hover:text-white">
            <ThemeIcon className="w-4 h-4" />
          </button>
          <button onClick={handleLogout} className="text-white/50 hover:text-white text-xs">Sign out</button>
        </div>
      </div>

      <main className="flex-1 overflow-x-hidden pt-14 md:pt-0">
        <div className="px-6 md:px-8 pt-4 max-w-7xl mx-auto flex items-center justify-end">
          <CohortSwitcher tone="dark" />
        </div>
        <div className="p-6 md:p-8 pt-4 max-w-7xl mx-auto">{children}</div>
        <div className="border-t border-white/[0.06] py-5 mt-4 mb-16 md:mb-0">
          <PortalSwitcher current="banker" tone="dark" />
        </div>
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#111111] border-t border-white/[0.06] flex items-center justify-around py-2 z-40">
          {nav.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const active = item.exact ? location === item.href : location.startsWith(item.href);
            return (
              <Link key={item.href} href={item.href}>
                <a className={`flex flex-col items-center gap-0.5 px-2 py-1 ${active ? "text-[#FF5C00]" : "text-white/50"}`}>
                  <Icon className="w-4 h-4" />
                  <span className="text-[9px]">{item.label}</span>
                </a>
              </Link>
            );
          })}
        </nav>
      </main>
    </div>
    </CohortProvider>
  );
}
